import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Scale, Download, Loader2, TrendingDown, TrendingUp, ListChecks } from 'lucide-react';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });

export default function FulfillmentVarianceReport() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const params = useCallback(() => {
    const p = {};
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  }, [dateFrom, dateTo]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reports/fulfillment-variance`, { headers: authHeaders(), params: params() });
      setRows(res.data.rows || []);
      setSummary(res.data.summary || null);
    } catch (e) {
      toast.error('Failed to load fulfillment variance report');
    }
    setLoading(false);
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const downloadXlsx = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/reports/fulfillment-variance.xlsx`, {
        headers: authHeaders(), params: params(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fulfillment_variance_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error('Excel download failed');
    }
    setDownloading(false);
  };

  return (
    <div className="space-y-4" data-testid="fulfillment-variance-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Scale className="w-6 h-6 text-teal-600" />
          <div>
            <h1 className="text-xl font-bold text-slate-800">Fulfillment Variance Report</h1>
            <p className="text-xs text-slate-500">Ordered vs Supplied deltas from QA quantity adjustments</p>
          </div>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="text-[11px] text-slate-500 block">From</label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 w-36" data-testid="fv-date-from" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500 block">To</label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 w-36" data-testid="fv-date-to" />
          </div>
          <Button onClick={downloadXlsx} disabled={downloading || loading} className="gap-2 bg-teal-600 hover:bg-teal-700" data-testid="fv-export-xlsx">
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Excel
          </Button>
          <CsvDownloadButton onDownload={downloadXlsx} testId="fv-export-csv" size="default" disabled={loading} />
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card><CardContent className="p-4 flex items-center gap-3">
            <ListChecks className="w-8 h-8 text-slate-500" />
            <div><div className="text-2xl font-bold" data-testid="fv-total-lines">{summary.total_variance_lines}</div>
              <div className="text-xs text-slate-500">Variance lines</div></div>
          </CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3">
            <TrendingDown className="w-8 h-8 text-amber-600" />
            <div><div className="text-2xl font-bold text-amber-700" data-testid="fv-under">{summary.total_under_shipped}</div>
              <div className="text-xs text-slate-500">Qty under-shipped</div></div>
          </CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-blue-600" />
            <div><div className="text-2xl font-bold text-blue-700" data-testid="fv-over">{summary.total_over_shipped}</div>
              <div className="text-xs text-slate-500">Qty over-shipped</div></div>
          </CardContent></Card>
        </div>
      )}

      {summary && summary.by_reason.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {summary.by_reason.map(r => (
            <Badge key={r.reason} variant="outline" className="text-xs">{r.reason}: {r.lines}</Badge>
          ))}
        </div>
      )}

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                {['Order #', 'Date', 'Franchise', 'Product ID', 'Product', 'Ordered', 'Supplied', 'Difference', 'Fulfillment', 'Reason'].map(h => (
                  <th key={h} className="px-3 py-2 text-left font-semibold text-slate-700 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={10} className="px-3 py-10 text-center text-slate-400">Loading…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={10} className="px-3 py-10 text-center text-slate-400" data-testid="fv-empty">
                  No fulfillment variances found — all supplied quantities matched orders.
                </td></tr>
              )}
              {!loading && rows.map((r, i) => (
                <tr key={i} className="border-t hover:bg-slate-50" data-testid={`fv-row-${i}`}>
                  <td className="px-3 py-2 font-mono text-xs">{r.order_number}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.order_date}</td>
                  <td className="px-3 py-2">{r.franchise_name}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.product_id}</td>
                  <td className="px-3 py-2">{r.product_name}</td>
                  <td className="px-3 py-2 text-right">{r.ordered_qty}</td>
                  <td className="px-3 py-2 text-right">{r.supplied_qty}</td>
                  <td className={`px-3 py-2 text-right font-semibold ${r.difference < 0 ? 'text-amber-600' : 'text-blue-600'}`}>
                    {r.difference > 0 ? '+' : ''}{r.difference}
                  </td>
                  <td className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{r.fulfillment.replaceAll('_', ' ')}</Badge></td>
                  <td className="px-3 py-2 text-xs text-slate-500">{r.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {summary && summary.top_under_shipped_products.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h2 className="text-sm font-semibold text-slate-700 mb-2">Top under-shipped SKUs (net variance)</h2>
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>{['Product ID', 'Product', 'Lines', 'Ordered', 'Supplied', 'Net Variance'].map(h => (
                  <th key={h} className="px-3 py-1.5 text-left font-semibold text-slate-600">{h}</th>))}
                </tr>
              </thead>
              <tbody>
                {summary.top_under_shipped_products.map((p, i) => (
                  <tr key={p.product_id} className="border-t" data-testid={`fv-top-${i}`}>
                    <td className="px-3 py-1.5 font-mono text-xs">{p.product_id}</td>
                    <td className="px-3 py-1.5">{p.product_name}</td>
                    <td className="px-3 py-1.5">{p.lines}</td>
                    <td className="px-3 py-1.5">{p.ordered}</td>
                    <td className="px-3 py-1.5">{p.supplied}</td>
                    <td className={`px-3 py-1.5 font-semibold ${p.variance < 0 ? 'text-amber-600' : 'text-blue-600'}`}>
                      {p.variance > 0 ? '+' : ''}{p.variance}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
